/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { autorun } from 'mobx';
import * as React from 'react';
import { TokenSelectionStore } from 'src/luis/modules/labeling/utils/TokenSelectionStore';
import { Selection } from 'src/modules/labeling/types/Selection';

export type UseSelectionEffectInput = {
    /**
     * Mobx store that contains selection states.
     */
    tokenSelectionStore: TokenSelectionStore;

    /**
     * Callback that gets called when the selection changes.
     */
    onSelect: (selection?: Selection) => void;
};

/**
 * Hook for synchronizing selection state with the its corresponding callback function.
 */
export const useSelectionEffect = (input: UseSelectionEffectInput) => {
    const { onSelect, tokenSelectionStore } = input;

    const onSelectRef = React.useRef<(selection?: Selection) => void>(onSelect);

    onSelectRef.current = onSelect;

    React.useEffect(() => {
        let prevSelection: Selection = [-1, -1];

        return autorun(() => {
            const { selection } = tokenSelectionStore;
            const { isDragging } = tokenSelectionStore;

            const setPrev = () => {
                prevSelection = selection;
            };

            if (prevSelection?.[0] !== -1 && prevSelection?.[1] !== -1 && selection[0] === -1 && selection[1] === -1) {
                setPrev();
                onSelectRef.current();

                return;
            }

            if ((selection[0] === -1 && selection[1] === -1) || isDragging) {
                setPrev();

                return;
            }

            setPrev();
            onSelectRef.current(selection);
        });
    }, [tokenSelectionStore]);
};
